'use client';

import { useRouter } from 'next/navigation';
import { useCallback } from 'react';

/**
 * Tracks whether the user has performed at least one client-side navigation
 * within the app during this page-load (tab) session.
 *
 * This is the reliable signal for "is there an in-app page behind me?".
 * `window.history.length` is NOT — it counts entries from before the user ever
 * reached our app (the email client, a Google search, a social feed, the OAuth
 * provider), so on a deep link it is usually > 1 and `router.back()` walks the
 * user straight OUT of the app to that external referrer.
 *
 * Module-level state is intentional: it resets on every full page load, which
 * is exactly the deep-link case we want to detect (depth starts at 0). It is
 * advanced by {@link markInAppNavigation}, mounted once via
 * `NavigationHistoryTracker` in the locale layout.
 */
let hasNavigatedInApp = false;

/** Called by NavigationHistoryTracker on each client-side route change. */
export function markInAppNavigation(): void {
  hasNavigatedInApp = true;
}

/** True once the user has navigated within the app in this tab session. */
export function hasInAppHistory(): boolean {
  return hasNavigatedInApp;
}

/**
 * Returns a back handler that calls `router.back()` only when there is a known
 * in-app page behind the user; otherwise (a deep-link / shared-URL entry) it
 * navigates to `fallbackHref`, keeping the user inside the app instead of
 * sending them to a dead external history entry.
 */
export function useBackNavigation(fallbackHref: string): () => void {
  const router = useRouter();

  return useCallback(() => {
    if (hasInAppHistory()) {
      router.back();
    } else {
      router.push(fallbackHref);
    }
  }, [router, fallbackHref]);
}
